import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { PersonFactsItem, PersonGet, PersonPhoneNumberItem, PersonService } from '@app/services/person.service';

@Component({
    selector: 'tt-person',
    templateUrl: './person.component.html',
    styleUrls: ['./person.component.css'],
})
export class PersonComponent implements OnInit, OnChanges {
    @Input()
    public params?: GetPersonPageParams;

    public get?: PersonGet;

    @Output()
    public submit = new EventEmitter<{ type: 'save' | 'delete'; data: PersonGet }>();

    public phoneNumbers: PersonPhoneNumberItem[] = [];

    public facts: PersonFactsItem[] = [];

    public saving = false;

    public deleting = false;

    constructor(
        private person: PersonService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async save() {
        if (!this.get) return;

        try {
            this.saving = true;

            await this.person.savePerson({ ...this.get, phonenumbers: this.phoneNumbers, persfacts: this.facts });

            if (this.gotoEdiSetup) {
                this.navigator?.go({ to: 'edisetup', parms: { argtype: 'cust_no', argvalue: this.get.cust_no, new_customer: true } });
            } else {
                this.navigator?.back();
            }

            this.submit.emit({ type: 'save', data: this.get });
        } catch (error) {
            console.error(error);
        } finally {
            this.saving = false;
        }
    }

    public async deletePerson() {
        if (!this.get) return;

        try {
            const confirmed = await this.modal.openConfirmDialog({ type: 'warning', title: 'confirm_delete', translateTitle: true, message: 'tt_confirm_delete_person', translateMessage: true });

            if (confirmed) {
                this.deleting = true;

                await this.person.deletePerson(this.get);

                this.navigator?.back();
                this.submit.emit({ type: 'delete', data: this.get });
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.deleting = false;
        }
    }

    private async loadGet() {
        if (!!this.params && typeof this.params === 'object' && 'cust_no' in this.params && 'pers_no' in this.params && !!this.params.cust_no && this.params.pers_no) {
            this.get = await this.person.getPerson(this.params);
        }
    }

    private async loadPhoneNumbers() {
        if (!!this.params && typeof this.params === 'object' && 'pers_no' in this.params && this.params.pers_no) {
            this.phoneNumbers = await this.person.getPersonsPhoneNumbers(this.params);
        }
    }

    private async loadFacts() {
        if (!!this.params && typeof this.params === 'object' && 'pers_no' in this.params && this.params.pers_no) {
            this.facts = await this.person.getPersonFacts(this.params);
        }
    }

    private async init() {
        this.loadPhoneNumbers();
        this.loadFacts();
        await this.loadGet();
        this.navigator?.setHeading?.(this.get?.webpage_heading ?? '');
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ width: 'min(180rem, 98vw)', height: '95vh' });
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }

    private get gotoEdiSetup() {
        return !!this.params && !!this.get && this.get.goto_edi_setup_after_saving_new_person === '1' && this.params.new_customer !== null && this.params.new_customer !== undefined && ['1', 'true', 'yes'].includes('' + this.params.new_customer);
    }
}

interface GetPersonPageParams {
    cust_no: string;
    pers_no: string;
    new_customer?: unknown;
}
